import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  ExternalLink,
  Eye,
  FileText,
  Loader2,
  Upload,
} from "lucide-react";
import { API } from "../Sidebar";

const GEO_API = `${API}/geo`;
const PREVIEW_URL = (path) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/geo/preview-by-path?path=${encodeURIComponent(path)}`;

const CATEGORIES = [
  "Water Damage",
  "Storm Season",
  "Fire Damage",
  "Mold",
  "Insurance & Claims",
  "Case Studies",
  "Field Notes",
];

const initialForm = {
  html: "",
  slug: "",
  category: "Water Damage",
  read_time: "",
  excerpt: "",
  author_name: "A-Star Restoration Team",
  publish_now: true,
};

export const BlogPostUpload = ({ onPublished }) => {
  const [form, setForm] = useState(initialForm);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(html?|txt)$/i)) {
      toast.error("Please upload a .html or .txt file.");
      return;
    }
    const text = await file.text();
    setForm((f) => ({ ...f, html: text }));
    setFileName(file.name);
    toast.success(`Loaded ${file.name} (${(text.length / 1024).toFixed(1)} KB)`);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.html || form.html.trim().length < 100) {
      toast.error("HTML content is required (min 100 chars).");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        html: form.html,
        publish_now: form.publish_now,
      };
      if (form.slug) payload.slug = form.slug.trim();
      if (form.category) payload.category = form.category;
      if (form.read_time) payload.read_time = form.read_time.trim();
      if (form.excerpt) payload.excerpt = form.excerpt.trim();
      if (form.author_name) payload.author_name = form.author_name.trim();

      const { data } = await axios.post(`${GEO_API}/blog/import`, payload);
      setResult(data);
      if (data.published) {
        toast.success(`Published to WordPress: ${data.slug}`);
      } else if (data.publish_error) {
        toast.error(`Saved but publish failed: ${data.publish_error}`);
      } else {
        toast.success(`Draft saved: ${data.slug}`);
      }
      onPublished?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Import failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setFileName("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Card className="p-6 space-y-5" data-testid="blog-upload-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Upload className="w-5 h-5 text-orange-500" /> Upload a Blog Post
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Paste HTML or upload a <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">.html</code> file. Title, featured image and excerpt are auto-extracted — no manual entry required.
          </p>
        </div>
        {result && (
          <Button variant="outline" size="sm" onClick={resetForm} data-testid="blog-upload-reset">
            Upload another
          </Button>
        )}
      </div>

      <form onSubmit={onSubmit} className="space-y-5">
        {/* File upload zone */}
        <div>
          <Label className="text-sm font-medium text-slate-700">Article HTML *</Label>
          <div className="mt-1.5 border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-orange-400 transition-colors bg-slate-50/50">
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,.txt"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              id="blog-html-file"
              data-testid="blog-upload-file"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label
                htmlFor="blog-html-file"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium cursor-pointer hover:bg-slate-100 hover:border-slate-400"
              >
                <Upload className="w-4 h-4" /> Choose .html file
              </label>
              <span className="text-sm text-slate-500">
                {fileName || "or paste HTML in the box below"}
              </span>
            </div>
          </div>
          <Textarea
            value={form.html}
            onChange={(e) => setForm({ ...form, html: e.target.value })}
            placeholder='<h1>Article title</h1><p>Body...</p>'
            rows={7}
            className="mt-3 font-mono text-xs"
            data-testid="blog-upload-html-textarea"
          />
        </div>

        {/* Metadata row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="blog-slug" className="text-sm font-medium text-slate-700">
              Slug <span className="text-slate-400 font-normal">(auto if empty)</span>
            </Label>
            <Input
              id="blog-slug"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="my-article-slug"
              className="mt-1.5 font-mono text-sm"
              data-testid="blog-upload-slug"
            />
          </div>
          <div>
            <Label htmlFor="blog-category" className="text-sm font-medium text-slate-700">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger id="blog-category" className="mt-1.5" data-testid="blog-upload-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="blog-read-time" className="text-sm font-medium text-slate-700">
              Read Time <span className="text-slate-400 font-normal">(auto if empty)</span>
            </Label>
            <Input
              id="blog-read-time"
              value={form.read_time}
              onChange={(e) => setForm({ ...form, read_time: e.target.value })}
              placeholder="8 min read"
              className="mt-1.5 text-sm"
              data-testid="blog-upload-read-time"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="blog-author" className="text-sm font-medium text-slate-700">Author</Label>
            <Input
              id="blog-author"
              value={form.author_name}
              onChange={(e) => setForm({ ...form, author_name: e.target.value })}
              className="mt-1.5 text-sm"
              data-testid="blog-upload-author"
            />
          </div>
          <div>
            <Label htmlFor="blog-excerpt" className="text-sm font-medium text-slate-700">
              Excerpt <span className="text-slate-400 font-normal">(auto if empty · ~180 chars)</span>
            </Label>
            <Input
              id="blog-excerpt"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="Short meta description..."
              className="mt-1.5 text-sm"
              data-testid="blog-upload-excerpt"
            />
          </div>
        </div>

        {/* Publish toggle + submit */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-100">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.publish_now}
              onChange={(e) => setForm({ ...form, publish_now: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300"
              data-testid="blog-upload-publish-toggle"
            />
            <span>Publish to WordPress immediately (also refreshes <code className="text-xs bg-slate-100 px-1 rounded">/blog/</code>)</span>
          </label>
          <Button type="submit" disabled={busy} className="min-w-[160px]" data-testid="blog-upload-submit">
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working...</>
            ) : form.publish_now ? (
              <><Upload className="w-4 h-4 mr-2" /> Import &amp; Publish</>
            ) : (
              <><FileText className="w-4 h-4 mr-2" /> Save as Draft</>
            )}
          </Button>
        </div>
      </form>

      {/* Result card */}
      {result && (
        <Card className="p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-200" data-testid="blog-upload-result">
          <div className="flex items-start gap-4">
            {result.featured_image && (
              <img
                src={result.featured_image}
                alt=""
                className="w-32 h-24 object-cover rounded-md border border-slate-200 flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                {result.published ? (
                  <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Published
                  </Badge>
                ) : result.publish_error ? (
                  <Badge variant="destructive" className="text-xs">Publish failed</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Draft saved</Badge>
                )}
                <Badge variant="outline" className="text-xs">{result.category}</Badge>
                <span className="text-xs text-slate-500">
                  {result.read_time} · {result.body_word_count} words
                </span>
              </div>
              <h4 className="font-semibold text-slate-900 truncate">{result.title}</h4>
              <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">{result.preview_path}</p>
              {result.excerpt && (
                <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{result.excerpt}</p>
              )}
              <div className="flex items-center gap-2 mt-3">
                <a
                  href={PREVIEW_URL(result.preview_path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 px-2.5 py-1 border border-slate-300 rounded-md hover:bg-slate-50"
                  data-testid="blog-upload-preview-link"
                >
                  <Eye className="w-3 h-3" /> Preview render
                </a>
                {result.wp_link && (
                  <a
                    href={result.wp_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 px-2.5 py-1 border border-emerald-300 bg-white rounded-md hover:bg-emerald-50"
                    data-testid="blog-upload-live-link"
                  >
                    <ExternalLink className="w-3 h-3" /> View live on astarrestoration.com
                  </a>
                )}
              </div>
              {result.publish_error && (
                <p className="text-xs text-red-600 mt-2 font-mono">{result.publish_error}</p>
              )}
            </div>
          </div>
        </Card>
      )}
    </Card>
  );
};

export default BlogPostUpload;
